import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, signal, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { Table } from 'primeng/table';
import { DialogService } from 'primeng/dynamicdialog';
import { Subscription, lastValueFrom } from 'rxjs';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { getControlErrors, isControlInvalid } from 'src/app/shared/services/validation-helpers';
import { greaterThanZero } from '../../../wellbore-inputs/components/well-configuration/validation/greaterThanZero';
import { PipesCatalog, PipesCatalogUi } from '../../models/pipes-catalog.model';
import { CatalogScope, CatalogFilterMode } from '../../models/catalog-scope.enum';
import { PipesService } from '../../shared/services/pipes-catalog.service';
import { innerSmallerThanOuterDiameter } from '../../validation/innerSmallerThanOuterDiameter';
import { outerGreaterThanInnerDiameter } from '../../validation/outerGreaterThanInnerDiameter';
import { driftLargerThanId } from '../../validation/driftLargerThanId';
import { checkForDuplicates } from '../../validation/checkForDuplicates';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { Utilities } from 'src/app/core/services/utilities';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { Store } from '@ngneat/elf';
import { isEqual } from 'lodash-es';
import ExcelJS from "exceljs";
import { saveAs } from 'file-saver-es';
import { UserRoles } from 'src/app/core/components/user-admin-page/user-model';
import { cannotDeleteCatalogItem } from '../../shared/catalog-helpers';

@Component({
  selector: 'app-pipes-catalog',
  templateUrl: './pipes-catalog.component.html',
  providers: [DialogService, ConfirmationService],
  styles: `
    .catalog-row-disabled > td:not(:first-child) {
      opacity: 0.6;
      pointer-events: none;
    }
  `,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PipesCatalogComponent implements OnInit, OnDestroy {

  @ViewChild('catalogTable') catalogTable: Table;

  private _subscriptions: Subscription = new Subscription();
  private _pipes: PipesCatalog[];
  private _filteredPipes: PipesCatalog[] = [];
  public pipeOds: SelectItem[] = [];
  public pipeTypes: SelectItem[] = [
    { label: 'Tubular Pipes', value: 'tubularPipe' },
    { label: 'Drill Pipes', value: 'drillPipe' },
    { label: 'HWDPs', value: 'hwdp' },
    { label: 'Collars', value: 'collars' }
  ];
  private discriminator: string;
  public diameterUnit: string;

  public columnDefs: any[];
  public isLoading = signal<boolean>(true);
  public pipesForm: UntypedFormGroup;
  public tableHeight = signal<string>('600px');
  public formIsValid = signal<boolean>(true);
  public weightUnit: string;
  public filterPipeOds = signal<SelectItem[]>([]);
  public paginatorFirst = signal<number>(0);

  // Scope filtering
  public CatalogScope = CatalogScope;
  public selectedScopeFilter: CatalogFilterMode = CatalogFilterMode.AllItems;
  public userRoles = signal<UserRoles | undefined>(undefined);
  public rows = 15; 

  get pipesCatalog(): UntypedFormArray {
    return this.pipesForm.get("pipes") as UntypedFormArray;
  }

  // Validation delegates
  public isControlInvalid: Function = isControlInvalid;
  public getControlErrors: Function = getControlErrors;

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.pipesCatalogStore = this._storeService.createStore(this.componentId, new PipesCatalogUi);
    this.selectedType = this.pipesCatalogStore.state.selectedType || 'tubularPipe';
    this.selectedOd = this.pipesCatalogStore.state.selectedOd;
  }
  get componentId(): string {
    return this._componentId;
  }
  public pipesCatalogStore: Store;

  // Local properties synced from store
  public selectedType: string = 'tubularPipe';
  public selectedOd: any = null;

  constructor(
    private _messenger: MediatorService,
    private _pipesCatalogService: PipesService,
    private _formBuilder: UntypedFormBuilder,
    private _confirmationService: ConfirmationService,
    private _storeService: StoreService
  ) {
    this.pipesForm = this._formBuilder.group({
      pipes: this._formBuilder.array([]),
    });
  }

  async ngOnInit(): Promise<void> {
    this.userRoles.set(await this._storeService.get<UserRoles>(StorageKeys.ROLES) ?? undefined);
    const uu = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.diameterUnit = uu.shortLengths;
    this.weightUnit = unitsLib[uu.linearDensity].symbol;

    this.columnDefs = [
      { field: 'outsideDiameter', header: `OD (${uu.shortLengths})` },
      { field: 'insideDiameter', header: `ID (${uu.shortLengths})` },
      { field: 'weightPerFoot', header: `Weight (${this.weightUnit})` },
      { field: 'driftDiameter', header: `Drift (${uu.shortLengths})` },
      { field: 'wallThickness', header: `Wall (${uu.shortLengths})` },
    ];

    this.getPipes(this.selectedType || 'tubularPipe');

    // Track FormArray validity with signal for OnPush change detection
    this._subscriptions.add(
      this.pipesCatalog.statusChanges.subscribe(() => {
        this.formIsValid.set(this.pipesCatalog.valid);
      })
    );

    addEventListener("keydown", (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    });
  }

  public getPipes(discriminator: string) {
    this.isLoading.set(true);
    this.selectedType = discriminator;
    this.paginatorFirst.set(0);
    this.pipesCatalogStore.update(state => ({ ...state, selectedType: discriminator }));
    this._pipesCatalogService.getPipes("", this.selectedScopeFilter).subscribe(pipes => {
      this.discriminator = discriminator;
      this._pipes = pipes;

      this.setPipeOds();
      if (this.catalogTable) {
        this.catalogTable.first = 0;
      }
      this.isLoading.set(false);
    });
  }

  public onScopeFilterChange(): void {
    this.pipesCatalog.clear();
    this.getPipes(this.discriminator || 'tubularPipe');
  }

  public onPageChange(event: { first?: number }): void {
    this.paginatorFirst.set(event.first ?? 0);
  }

  public onAddPipe(idx?: number): void {
    this.pipesCatalog.insert(idx || 0, this.newPipe());
    this.paginatorFirst.set(0);
    if (this.catalogTable) {
      this.catalogTable.first = 0;
      this.catalogTable.scrollTo?.({ top: 0, behavior: 'smooth' });
    }
  }

  public onDeletePipe(pipeFg: UntypedFormGroup): void {
    // Find the actual index of this FormGroup in the FormArray
    const idx = this.pipesCatalog.controls.indexOf(pipeFg);
    if (idx === -1) {
      return; // Safety check - shouldn't happen
    }

    const pipeId = pipeFg.get("id").value;
    const pipeOuterDiameter = pipeFg.get("outsideDiameter").value;
    const diameterUnitShort = this.diameterUnit == 'in' ? '"' : this.diameterUnit;

    if (pipeId.length < 36) {
      // Unsaved item - just remove from FormArray
      this.pipesCatalog.removeAt(idx);
      // Trigger change detection for OnPush
      this.isLoading.set(true);
      setTimeout(() => this.isLoading.set(false), 0);
    } else {
      this._confirmationService.confirm({
        message: `Are you sure that you want to delete the selected ${pipeOuterDiameter + diameterUnitShort} pipe?`,
        accept: async () => {
          if (pipeId.length > 24) { // it's been saved to the db, so delete through API
            await lastValueFrom(this._pipesCatalogService.deletePipe(pipeId));
            this._messenger.publish(new DataSavedMessage('Pipes Catalog'));
          }
          this.pipesCatalog.removeAt(idx);
          // Remove the correct item from master array by ID
          const masterIdx = this._pipes.findIndex(p => p.id === pipeId);
          if (masterIdx >= 0) {
            this._pipes.splice(masterIdx, 1);
          }
          this.setPipeOds();
          this.filterByOd({ value: this.selectedOd });
        }
      });
    }
  }

  public deleteItemCheck(itemFg: FormGroup): boolean {
    return cannotDeleteCatalogItem(itemFg, this.userRoles());
  }

  public getScopeLabel(scope: CatalogScope): string {
    return scope === CatalogScope.Global ? 'Global' : 'User';
  }

  public getScopeBadgeClass(scope: CatalogScope): string {
    return scope === CatalogScope.Global ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800';
  }

  public async toggleScope(formGroup: UntypedFormGroup): Promise<void> {
    this.isLoading.set(true);
    const currentScope = formGroup.get('scope').value;
    const newScope = currentScope === CatalogScope.User ? CatalogScope.Global : CatalogScope.User;

    const pipeData = formGroup.value;
    pipeData.scope = newScope;

    await lastValueFrom(this._pipesCatalogService.updatePipe(new PipesCatalog(pipeData)));

    // Only update the form if save succeeds
    formGroup.get('scope').patchValue(newScope, { emitEvent: false });

    // Update the local cache
    const index = this._pipes.findIndex(x => x.id === pipeData.id);
    if (index > -1) {
      this._pipes[index].scope = newScope;
    }

    this._messenger.publish(new DataSavedMessage('Pipes Catalog'));
    this.isLoading.set(false);
  }

  private async handleSavePipeRow(v: any, pipeRecord: UntypedFormGroup): Promise<void> {
    this.revalidatePipeRow(pipeRecord);

    const existingPipe = this._pipes.find(p => p.id === v.id && p.insideDiameter === v.insideDiameter &&
      p.outsideDiameter === v.outsideDiameter && p.weightPerFoot === v.weightPerFoot &&
      p.driftDiameter === v.driftDiameter && p.scope === v.scope);
    if (existingPipe) {
      return;
    }

    if (pipeRecord.valid && !this.isLoading()) {
      let newEntityId: { id: string };

      if (typeof v.id === "string" && v.id.length === 24) {
        this.isLoading.set(true);
        newEntityId = await lastValueFrom(this._pipesCatalogService.addPipe(new PipesCatalog(v)));
        pipeRecord.get("id")?.patchValue(newEntityId, { emitEvent: false });
        this.isLoading.set(false);
      } else {
        await lastValueFrom(this._pipesCatalogService.updatePipe(new PipesCatalog(v)));
        newEntityId = v.id; // Retain the existing ID
      }
      this._messenger.publish(new DataSavedMessage('Pipes Catalog'));

      v.wallThickness = (v.outsideDiameter - v.insideDiameter) / 2;
      pipeRecord.get("id")?.patchValue(newEntityId, { emitEvent: false });
      const savedPipe = { ...pipeRecord.value, wallThickness: v.wallThickness };
      const savedIdx = this._pipes.findIndex(p => p.id === savedPipe.id);
      if (savedIdx >= 0) {
        this._pipes[savedIdx] = savedPipe;
      } else {
        this._pipes.unshift(savedPipe);
      }
      this.setPipeOds(false);
    }
  }

  private revalidatePipeRow(pipeRecord: UntypedFormGroup): void {
    ['outsideDiameter', 'insideDiameter', 'driftDiameter'].forEach(name =>
      pipeRecord.get(name)?.updateValueAndValidity({ onlySelf: true, emitEvent: false })
    );
    pipeRecord.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  private newPipe(): UntypedFormGroup {
    const pipeFg = new UntypedFormGroup({
      id: new UntypedFormControl(Utilities.generateFakeGuid()),
      outsideDiameter: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, outerGreaterThanInnerDiameter] }),
      insideDiameter: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, innerSmallerThanOuterDiameter] }),
      weightPerFoot: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, greaterThanZero] }),
      driftDiameter: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, driftLargerThanId] }),
      wallThickness: new UntypedFormControl({ value: '', disabled: true }),
      discriminator: new UntypedFormControl(this.discriminator),
      isLocked: new UntypedFormControl(false),
      metadata: new UntypedFormControl(null),
      scope: new UntypedFormControl(CatalogScope.User)
    }, checkForDuplicates);

    this._subscriptions.add(pipeFg.valueChanges.subscribe((v) => this.handleSavePipeRow(v, pipeFg)));
    this._subscriptions.add(pipeFg.valueChanges.subscribe(() => this.updateWallThickness(pipeFg)));

    pipeFg.markAllAsTouched();

    return pipeFg;
  }

  private updateWallThickness(pipeRecord: UntypedFormGroup) {
    const outsideDiameter = pipeRecord.get("outsideDiameter") as UntypedFormGroup;
    const insideDiameter = pipeRecord.get("insideDiameter") as UntypedFormGroup;
    const wallThickness = ((outsideDiameter.value - insideDiameter.value) / 2).toFixed(3);
    pipeRecord.controls.wallThickness.patchValue(wallThickness, { emitEvent: false });
  }

  private populateFormData(pipeData: PipesCatalog[]) {
    this.pipesCatalog.clear();
    pipeData.forEach((data, idx) => {
      const pipeFg = this.newPipe();
      this.pipesCatalog.push(pipeFg);
      this.pipesCatalog.controls[idx].patchValue({ ...data }, { emitEvent: false });
      this.syncGradeRowState(this.pipesCatalog.controls[idx] as FormGroup);
    });
  }

  private syncGradeRowState(pipeFg: FormGroup): void {
    if (this.updateFormDisabledState(pipeFg)) {
      pipeFg.disable({ emitEvent: false });
      return;
    }

    pipeFg.enable({ emitEvent: false });
  }

  public updateFormDisabledState(pipeFg: FormGroup): boolean {
    return this.deleteItemCheck(pipeFg) || this.userRoles()?.readOnly || false;
  }

  public filterByOd(e) {
    this.selectedOd = e.value;
    this.pipesCatalogStore.update(state => ({ ...state, selectedOd: e.value }));
    this._filteredPipes = this._pipes.filter(p =>
      e.value == 0
        ? p.discriminator == this.discriminator
        : p.outsideDiameter == e.value && p.discriminator == this.discriminator
    );

    this.populateFormData(this._filteredPipes);
    this.paginatorFirst.set(0);
    if (this.catalogTable) {
      this.catalogTable.first = 0;
      this.catalogTable.scrollTo?.({ top: 0, behavior: 'smooth' });
    }
  }

  private setPipeOds(applyFilter = true): void {
    const pipeOds = this._pipes.filter(p => p.discriminator == this.discriminator)
      .map(x => ({ label: x.outsideDiameter.toString(), value: x.outsideDiameter }));
    this.pipeOds = [...new Set(pipeOds.map(item => +item.value.toFixed(3)))].map(x => ({ label: x.toString(), value: x }));
    const filterPipeOds = [
      ...new Map(
        this.pipeOds
          .filter(x => this._pipes.some(c => +c.outsideDiameter.toFixed(3) === x.value))
          .map(x => [x.value, x])
      ).values()
    ];

    filterPipeOds.unshift({ label: 'View All', value: 0 });
    this.filterPipeOds.set(filterPipeOds);

    const previousOd = this.selectedOd;
    if (!this.selectedOd || !filterPipeOds.find(p => isEqual(p.value, this.selectedOd))) {
      this.selectedOd = filterPipeOds[0].value;
      this.pipesCatalogStore.update(state => ({ ...state, selectedOd: this.selectedOd }));
    }
    // Rebuilding the rows would destroy inputs the user is still editing, so after a row save only do it when the filter itself changed
    if ((applyFilter || previousOd !== this.selectedOd) && filterPipeOds.find(p => isEqual(p.value, this.selectedOd))) {
      this.filterByOd({ value: this.selectedOd });
    }
  }

  public async exportExcel(): Promise<void> {
    const filteredCatalog = this.pipesCatalog.controls.filter(td => td.value.discriminator == this.discriminator);
    const mappedData = filteredCatalog.map(td => {
      const objWithHeaderKeys = {};
      this.columnDefs.forEach(cd => {
        const objHeaderKey = cd.header;
        objWithHeaderKeys[objHeaderKey.replace(/["']/g, '"')] = td['controls'][cd.field].value;
      });
      return objWithHeaderKeys;
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Connections Catalog");

    worksheet.columns = Object.keys(mappedData[0]).map((key) => ({
      header: key,
      key: key,
      width: 45
    }));

    worksheet.addRows(mappedData);

    const buffer = await workbook.xlsx.writeBuffer();

    const catalogType = this.discriminator == 'tubularPipe' ? 'Tubular Pipes' : this.discriminator == 'drillPipe' ? 'Drill Pipes' : this.discriminator == 'hwdp' ? 'HWDPs' : 'Collars';
    this.saveAsExcelFile(buffer, `${catalogType} Catalog`);
  }

  public saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    saveAs(data, fileName + '_export_' + EXCEL_EXTENSION);
  }

  ngOnDestroy(): void {
    this._subscriptions?.unsubscribe();
  }
}
